import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, Plus, Trash2, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

function prettifyLabel(key) {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

export default function PreviewEditPage() {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [unlocked, setUnlocked] = useState(false);
    const [fields, setFields] = useState([]); // [{key, value}]
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [showAddField, setShowAddField] = useState(false);

    const inputRef = useRef(null);
    useEffect(() => { if (!unlocked) inputRef.current?.focus(); }, [unlocked]);

    const inputStyle = {
        background: 'var(--bg-secondary)',
        border: '2px solid var(--border)',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        fontFamily: 'monospace',
        fontSize: '20px',
        letterSpacing: '0.2em',
        textAlign: 'center',
        textTransform: 'uppercase',
        padding: '14px 16px',
        width: '100%',
        outline: 'none',
        boxSizing: 'border-box',
    };

    const fieldInputStyle = {
        background: 'var(--bg-secondary)',
        border: '2px solid var(--border)',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        fontFamily: 'inherit',
        fontSize: '14px',
        padding: '10px 14px',
        width: '100%',
        outline: 'none',
        boxSizing: 'border-box',
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/preview/content?code=${encodeURIComponent(code.trim())}`);
            if (!res.ok) {
                setError('Ungültiger Code.');
                setLoading(false);
                return;
            }
            const data = await res.json();
            setFields(Object.entries(data).map(([key, value]) => ({ key, value: String(value) })));
            setUnlocked(true);
        } catch {
            setError('Verbindungsfehler.');
        }
        setLoading(false);
    };

    const updateValue = (idx, value) => {
        setFields(prev => prev.map((f, i) => i === idx ? { ...f, value } : f));
    };

    const removeField = (idx) => {
        setFields(prev => prev.filter((_, i) => i !== idx));
    };

    const addField = () => {
        const key = newKey.trim().toLowerCase().replace(/\s+/g, '_');
        if (!key) return;
        if (fields.some(f => f.key === key)) { setNewKey(''); setShowAddField(false); return; }
        setFields(prev => [...prev, { key, value: '' }]);
        setNewKey('');
        setShowAddField(false);
    };

    const save = async () => {
        setSaving(true);
        const content = {};
        fields.forEach(f => { content[f.key] = f.value; });
        try {
            await fetch(`${API}/api/preview/content`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: code.trim(), content }),
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch { /* ignore */ }
        setSaving(false);
    };

    if (unlocked) {
        return (
            <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
                <div className="container">
                    <div style={{ maxWidth: '560px', margin: '0 auto' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <Edit3 size={22} color="var(--accent)" />
                            <h1 className="section-title" style={{ fontSize: '24px', marginBottom: 0 }}>Inhalte bearbeiten</h1>
                        </div>
                        <p className="section-subtitle" style={{ marginBottom: '28px' }}>
                            Ändere hier die Texte deiner Webseite. Änderungen sind sofort live.
                        </p>

                        {fields.length === 0 && !showAddField && (
                            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '14px', marginBottom: '20px' }}>
                                Noch keine Inhalte hinterlegt. Füge unten ein Feld hinzu.
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
                            {fields.map((f, idx) => (
                                <div key={f.key} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {prettifyLabel(f.key)}
                                        </label>
                                        <button onClick={() => removeField(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', padding: 0 }}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <textarea
                                        value={f.value}
                                        onChange={e => updateValue(idx, e.target.value)}
                                        style={{ ...fieldInputStyle, minHeight: '44px', resize: 'vertical' }}
                                    />
                                </div>
                            ))}
                        </div>

                        {showAddField ? (
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                                <input
                                    autoFocus
                                    value={newKey}
                                    onChange={e => setNewKey(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') addField(); }}
                                    placeholder="Name des neuen Felds, z.B. Kursdatum"
                                    style={fieldInputStyle}
                                />
                                <button onClick={addField} className="btn-primary" style={{ width: 'auto', padding: '10px 16px' }}>OK</button>
                                <button onClick={() => { setShowAddField(false); setNewKey(''); }} className="icon-button" style={{ color: 'var(--text-secondary)' }}>
                                    <X size={18} />
                                </button>
                            </div>
                        ) : (
                            <button onClick={() => setShowAddField(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1px dashed var(--border)',
                                    borderRadius: '10px', padding: '10px 16px', color: 'var(--text-secondary)', cursor: 'pointer',
                                    fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, marginBottom: '24px', width: '100%', justifyContent: 'center' }}>
                                <Plus size={15} /> Neues Feld hinzufügen
                            </button>
                        )}

                        <button onClick={save} disabled={saving} className="btn-primary" style={{ justifyContent: 'center', width: '100%' }}>
                            {saving ? 'Speichert…' : saved ? <><Check size={16} /> Gespeichert</> : 'Speichern'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="section" style={{ minHeight: 'calc(100vh - 140px)', display: 'flex', alignItems: 'center' }}>
            <div className="container">
                <div style={{ maxWidth: '380px', margin: '0 auto', textAlign: 'center' }}>
                    <div style={{
                        width: '56px', height: '56px', borderRadius: '50%',
                        background: 'rgba(251,146,60,0.1)', border: '1.5px solid var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 20px',
                    }}>
                        <Edit3 size={24} color="var(--accent)" />
                    </div>
                    <h1 className="section-title" style={{ fontSize: '26px', marginBottom: '8px' }}>Inhalte bearbeiten</h1>
                    <p className="section-subtitle" style={{ marginBottom: '32px' }}>
                        Gib deinen Zugangscode ein, um die Texte deiner Webseite zu bearbeiten.
                    </p>

                    <AnimatePresence>
                        {error && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                style={{
                                    padding: '12px 16px', marginBottom: '16px', overflow: 'hidden',
                                    background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)',
                                    borderRadius: '8px', color: '#ff5050', fontSize: '14px',
                                }}>{error}</motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={submit}>
                        <input
                            ref={inputRef}
                            style={inputStyle}
                            placeholder="CODE"
                            value={code}
                            onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
                            required
                        />
                        <button type="submit" className="btn-primary" disabled={loading}
                            style={{ justifyContent: 'center', width: '100%', marginTop: '16px' }}>
                            {loading ? 'Prüfe…' : 'Weiter'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
